import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useProgress } from "@/lib/hooks";
import { PageShell } from "@/components/AppShell";
import { Loader } from "@/components/ProtectedRoute";
import { EvidenceWorkspace, GovernedQuery } from "@/components/CapstoneEvidence";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";



const Choice = ({ item, value, onChange }) => (
  <fieldset className="space-y-2">
    <legend className="text-sm font-semibold">{item.label}</legend>
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.entries(item.options).map(([k, l]) => (
        <label key={k} className={`flex cursor-pointer items-start gap-2 border p-3 text-sm transition-colors ${value === k ? "border-brand-primary bg-brand-primary/10" : "border-border hover:bg-muted/50"}`}>
          <input type="radio" name={item.id} value={k} checked={value === k} onChange={() => onChange(item.id, k)} data-testid={`capstone-${item.id}-${k}`} className="mt-1 accent-[hsl(var(--brand-primary))]" />
          {l}
        </label>
      ))}
    </div>
  </fieldset>
);

const ResultPanel = ({ r, rubric }) => (
  <div data-testid="capstone-result" className="border border-border bg-card p-6">
    <div className="overline-label">Capstone result</div>
    <div data-testid="capstone-result-total" className="mt-2 font-heading text-5xl font-black">{r.total}%</div>
    <p className={`mt-1 font-semibold ${r.passed ? "text-brand-accent" : "text-destructive"}`}>{r.passed ? "Passed — Strategist credential issued." : "Below the pass evidence. Revise and resubmit."}</p>
    {r.critical_failures.length > 0 && (
      <div data-testid="capstone-critical-failures" className="mt-4 border-l-4 border-destructive p-3 text-sm">
        <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Critical safety failure</div>
        <ul className="mt-1 list-disc pl-5">{r.critical_failures.map((c) => <li key={c.message || c}>{c.message || c} {c.lesson_id && <Link to={`/lessons/${c.lesson_id}`} className="text-brand-primary underline">Review the lesson</Link>}</li>)}</ul>
      </div>
    )}
    <table className="mt-5 w-full text-sm">
      <tbody>{rubric.map((d) => <tr key={d.key} className="border-b border-border"><td className="py-2">{d.label}</td><td className="py-2 font-mono">{r.scores[d.key]} / {d.weight}</td><td className="py-2 text-xs text-muted-foreground">{r.feedback[d.key]}</td></tr>)}</tbody>
    </table>
    {r.preview_only && <p data-testid="capstone-preview-only" className="mt-4 text-sm text-muted-foreground">Admin preview: no certificate is issued for admin accounts.</p>}
    {r.certificate_id && <Link to={`/certificates/${r.certificate_id}`} data-testid="capstone-certificate-link" className="mt-5 inline-block rounded-sm bg-brand-action px-5 py-2.5 font-semibold text-brand-action-fg">View Strategist certificate →</Link>}
  </div>
);

export default function Capstone() {
  const qc = useQueryClient();
  const { data: progress } = useProgress();
  const { data } = useQuery({ queryKey: ["capstone"], queryFn: async () => (await api.get("/capstone/scenario")).data });
  const [sel, setSel] = useState({});
  const [flags, setFlags] = useState([]);
  const [texts, setTexts] = useState({});
  const [result, setResult] = useState(null);
  if (!data || !progress) return <Loader />;
  const { scenario: s, form } = data;
  const strat = progress.tiers.find((t) => t.tier === "strategist");
  const open = strat.status === "capstone_pending";
  const shown = result || strat.capstone;
  const pick = (id, v) => setSel({ ...sel, [id]: v });

  const submit = async () => {
    try {
      const { data: r } = await api.post("/capstone/submit", { selections: sel, flags, texts });
      setResult(r);
      qc.invalidateQueries({ queryKey: ["progress"] });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <PageShell wide>
      <div className="overline-label">Module 8 · Final Applied Project · synthetic data</div>
      <h1 data-testid="capstone-title" className="mt-2 font-heading text-4xl font-black tracking-tighter">{s.title}</h1>
      {!open && strat.status !== "certified" && <p data-testid="capstone-locked-note" className="mt-3 text-sm text-muted-foreground">Explore the evidence now. Submission opens once all eight Strategist-tier certified quizzes are passed.</p>}
      {shown && <div className="mt-8"><ResultPanel r={shown} rubric={s.rubric} /></div>}
      <div className="mt-10 grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-4">
          <div className="bg-trust p-6 text-white">
            <div className="font-mono text-[11px] uppercase tracking-widest text-white/60">{s.requester} asks</div>
            <p className="mt-3 font-serif text-2xl italic leading-snug">“{s.request}”</p>
            <p className="mt-4 text-xs text-white/60">{s.note}</p>
          </div>
          <div className="border border-border bg-card p-5">
            <div className="overline-label">Permissions boundary</div>
            {s.permissions.map((p) => (
              <div key={p.role} className="mt-3 text-sm"><div className="font-semibold">{p.role}</div><div className="text-muted-foreground">Can: {p.can.join(", ")}</div><div className="text-muted-foreground">Cannot: {p.cannot.join(", ")}</div></div>
            ))}
          </div>
          <div className="border border-border bg-card p-5">
            <div className="overline-label">Success criteria</div>
            <ul className="mt-3 space-y-1 text-sm">{s.rubric.map((r) => <li key={r.key} className="flex justify-between"><span>{r.label}</span><span className="font-mono">{r.weight}%</span></li>)}</ul>
            <p className="mt-3 text-xs text-muted-foreground">Pass: 80% overall with no critical safety failure.</p>
          </div>
        </div>
        <div className="space-y-6 lg:col-span-8">
          <EvidenceWorkspace s={s} />
          <GovernedQuery />
        </div>
      </div>
      <section className="mt-14 space-y-10">
        <h2 className="font-heading text-2xl font-bold">Your strategy brief · 4 steps · 30–45 min</h2>
        {form.steps.map((st, si) => (
          <div key={st.key} data-testid={`capstone-step-${st.key}`} className="space-y-5 border-t border-border pt-6">
            <h3 className="font-heading text-lg font-bold">{si + 1} · {st.title} <span className="font-mono text-xs font-normal text-muted-foreground">~{st.minutes} min</span></h3>
            {st.fields.map((item) => <Choice key={item.id} item={item} value={sel[item.id]} onChange={pick} />)}
            {st.flags && (
              <fieldset className="space-y-2">
                <legend className="text-sm font-semibold">Flag every historian tag that should not be trusted as-is</legend>
                <div className="grid gap-2 sm:grid-cols-3">
                  {s.historian.map((h) => h.tag).map((tag) => (
                    <label key={tag} className="flex items-center gap-2 border border-border p-3 font-mono text-xs">
                      <Checkbox data-testid={`capstone-flag-${tag}`} checked={flags.includes(tag)} onCheckedChange={(c) => setFlags(c ? [...flags, tag] : flags.filter((f) => f !== tag))} />{tag}
                    </label>
                  ))}
                </div>
              </fieldset>
            )}
            {st.text && <TextTask t={{ id: "brief", label: "Evidence-backed answer for the next shift: next approved check, owner, time window, uncertainty, pilot outcome (optional, 120+ characters recommended)", min: 120 }} texts={texts} setTexts={setTexts} />}
          </div>
        ))}
        <button data-testid="capstone-submit-button" disabled={!open} onClick={submit} className="rounded-sm bg-brand-action px-6 py-3 font-semibold text-brand-action-fg hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-45">Submit strategy brief</button>
      </section>
    </PageShell>
  );
}

const TextTask = ({ t, texts, setTexts }) => (
  <div className="space-y-2">
    <label className="text-sm font-semibold" htmlFor={`cap-${t.id}`}>{t.label}</label>
    <Textarea id={`cap-${t.id}`} data-testid={`capstone-text-${t.id}`} rows={5} className="rounded-sm" value={texts[t.id] || ""} onChange={(e) => setTexts({ ...texts, [t.id]: e.target.value })} />
    <div className="text-xs text-muted-foreground">{(texts[t.id] || "").length} / {t.min}+ characters</div>
  </div>
);
